<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import CoLivingTabs from './CoLivingTabs.vue';
import CondoModal from './components/CondoModal.vue';
import { apiErrorCode, apiErrorMessage } from '../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from './constants';
import { pageToStepBackTo } from './helpers';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SEARCH_DELAY_MS = 300;

export default {
  name: 'CoLivingCondosPage',
  components: {
    PageHeader,
    PaginationBar,
    LoadingState,
    DeleteModal,
    CoLivingTabs,
    CondoModal,
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      search: '',
      statusFilter: 'all',
      page: 1,
      pageSize: PAGE_SIZE_OPTIONS[0],
      loadError: '',
      isModalOpen: false,
      editingCondo: null,
      condoToDelete: null,
      isDeleting: false,
      searchTimer: null,
    };
  },
  computed: {
    ...mapGetters('coLivingCondos', {
      condos: 'getPage',
      meta: 'getMeta',
      uiFlags: 'getUiFlags',
    }),
    totalPages() {
      return Math.max(1, Math.ceil(this.meta.total / this.pageSize));
    },
    pagination() {
      return {
        currentPage: this.page,
        totalPages: this.totalPages,
        pageSize: this.pageSize,
      };
    },
    query() {
      const query = { page: this.page, page_size: this.pageSize };
      if (this.search.trim()) query.search = this.search.trim();
      if (this.statusFilter !== 'all') query.status = this.statusFilter;
      return query;
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    ...mapActions('coLivingCondos', ['fetchCondoPage', 'removeCondo']),
    async load() {
      this.loadError = '';
      try {
        const data = await this.fetchCondoPage(this.query);
        const back = pageToStepBackTo(data, this.page, this.pageSize);
        if (back) {
          this.page = back;
          await this.load();
        }
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    onFilterChange() {
      this.page = 1;
      this.load();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.onFilterChange, SEARCH_DELAY_MS);
    },
    goToPage(page) {
      this.page = page;
      this.load();
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.onFilterChange();
    },
    openAdd() {
      this.editingCondo = null;
      this.isModalOpen = true;
    },
    openEdit(condo) {
      this.editingCondo = condo;
      this.isModalOpen = true;
    },
    openDetails(condo) {
      this.$router.push({
        name: 'co_living_condo',
        params: { accountId: this.$route.params.accountId, condoId: condo.id },
      });
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await this.removeCondo(this.condoToDelete.id);
        useAlert(this.$t('CO_LIVING.CONDOS.DELETED'));
        this.load();
      } catch (err) {
        useAlert(
          apiErrorCode(err) === 'in_use'
            ? this.$t('CO_LIVING.CONDOS.IN_USE')
            : apiErrorMessage(this, err, 'CO_LIVING.CONDOS.DELETE_FAILED')
        );
      } finally {
        this.isDeleting = false;
        this.condoToDelete = null;
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-condos-page">
    <PageHeader
      :title="$t('CO_LIVING.TITLE')"
      icon="condo"
      :description="$t('CO_LIVING.DESCRIPTION')"
    />
    <CoLivingTabs />

    <div class="flex items-start justify-between gap-4 mb-2">
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CO_LIVING.CONDOS.INTRO') }}
      </p>
      <woot-button
        icon="add"
        data-testid="co-living-add-condo"
        @click="openAdd"
      >
        {{ $t('CO_LIVING.CONDOS.ADD_CONDO') }}
      </woot-button>
    </div>
    <p class="mb-4 text-xs text-slate-500 dark:text-slate-400">
      {{ $t('CO_LIVING.CONDOS.ADDRESS_NOTE') }}
    </p>

    <div class="flex flex-wrap gap-3 mb-4">
      <input
        v-model="search"
        type="search"
        :placeholder="$t('CO_LIVING.CONDOS.SEARCH')"
        class="max-w-xs"
        :class="[fieldClass]"
        data-testid="co-living-condo-search"
        @input="onSearchInput"
      />
      <select
        v-model="statusFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-condo-status-filter"
        @change="onFilterChange"
      >
        <option value="all">{{ $t('CO_LIVING.COMMON.ALL_STATUSES') }}</option>
        <option value="active">{{ $t('CO_LIVING.COMMON.ACTIVE') }}</option>
        <option value="archived">{{ $t('CO_LIVING.COMMON.ARCHIVED') }}</option>
      </select>
    </div>

    <LoadingState
      v-if="uiFlags.isFetchingPage && !condos.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>
    <p
      v-else-if="!condos.length"
      class="text-sm text-slate-500 dark:text-slate-400"
      data-testid="co-living-condos-empty"
    >
      {{ $t('CO_LIVING.CONDOS.EMPTY') }}
    </p>
    <template v-else>
      <div
        class="overflow-x-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <table class="w-full text-sm" data-testid="co-living-condos-table">
          <thead>
            <tr class="text-left text-slate-500 dark:text-slate-400">
              <th class="p-3">{{ $t('CO_LIVING.CONDOS.COLUMNS.CONDO') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.CONDOS.COLUMNS.LOCATION') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.CONDOS.COLUMNS.UNITS') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.CONDOS.COLUMNS.TENANTS') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.CONDOS.COLUMNS.OPEN') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.COMMON.STATUS') }}</th>
              <th class="p-3" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="condo in condos"
              :key="condo.id"
              class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              :data-testid="`co-living-condo-row-${condo.id}`"
            >
              <td class="p-3">
                <button
                  type="button"
                  class="font-medium text-woot-600 dark:text-woot-400 hover:underline text-left"
                  :data-testid="`co-living-condo-open-${condo.id}`"
                  @click="openDetails(condo)"
                >
                  {{ condo.name }}
                </button>
                <span class="block text-xs text-slate-500">
                  {{ condo.address }}
                </span>
              </td>
              <td class="p-3">
                <span
                  v-if="!condo.located"
                  class="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300"
                >
                  {{ $t('CO_LIVING.CONDOS.NOT_LOCATED') }}
                </span>
                <span v-else class="text-xs text-slate-500">
                  {{ condo.lat }}, {{ condo.lng }}
                </span>
              </td>
              <td class="p-3">{{ condo.unit_count }}</td>
              <td class="p-3">{{ condo.tenant_count }}</td>
              <td class="p-3">{{ condo.open_ticket_count }}</td>
              <td class="p-3">
                {{
                  condo.active
                    ? $t('CO_LIVING.COMMON.ACTIVE')
                    : $t('CO_LIVING.COMMON.ARCHIVED')
                }}
              </td>
              <td class="p-3 text-right whitespace-nowrap">
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                  variant="clear"
                  size="small"
                  icon="edit"
                  :data-testid="`co-living-condo-edit-${condo.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                  @click="openEdit(condo)"
                />
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                  variant="clear"
                  size="small"
                  color-scheme="alert"
                  icon="delete"
                  :data-testid="`co-living-condo-delete-${condo.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                  @click="condoToDelete = condo"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <PaginationBar
        :pagination="pagination"
        :page-size-options="pageSizeOptions"
        :loading="uiFlags.isFetchingPage"
        @previous="goToPage(page - 1)"
        @next="goToPage(page + 1)"
        @page-size-change="onPageSizeChange"
      />
    </template>

    <CondoModal
      v-if="isModalOpen"
      :condo="editingCondo"
      @close="isModalOpen = false"
      @saved="load"
    />
    <DeleteModal
      v-if="condoToDelete"
      :delete-name="condoToDelete.name"
      :is-loading="isDeleting"
      @close="condoToDelete = null"
      @confirm="confirmDelete"
    >
      <p class="mt-2 text-sm text-slate-500">
        {{ $t('CO_LIVING.CONDOS.DELETE_NOTE') }}
      </p>
    </DeleteModal>
  </div>
</template>
